import type { APIResult } from '@/backend/types'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import type { LibraryFile } from '@/modules/Library/libraryStore'
import type {
  DataField,
  Field,
  FileCollectionField,
  FileField,
} from '@/modules/Project/Fields/types'
import { promiseQueue } from '@/modules/Workspaces/KnowledgeHub/Files/promiseQueue'
import { toast } from '@/shared/toast'
import { useSetFieldValue } from '@/shared/useSetFieldValue'
import { invariant } from '@/shared/utils/typeAssertions'

type FieldWithFile = FileField | FileCollectionField | DataField

const maxConcurrent = 10
const maxBatchSize = 100

export function useFileUrlUpload() {
  return { uploadLibraryFiles, uploadFromUrl }
}
function uploadLibraryFiles(props: { field: FieldWithFile; files: LibraryFile[] }) {
  const { field } = props
  const [firstFile, ...otherFiles] = props.files
  invariant(firstFile)
  if (props.files.length > maxBatchSize) {
    toast.error(`You can upload up to ${maxBatchSize} files at a time`)
    return
  }

  // eslint-disable-next-line internal/no-call-composable-outside-setup
  const setFieldValue = useSetFieldValue()

  const uploadFirstFile = () => {
    return setFieldValue({
      [field.propertyId]: {
        field,
        newValue: { file_name: firstFile.filename, file_url: firstFile.fileUrl },
      },
    })
  }

  // eslint-disable-next-line internal/no-call-composable-outside-setup
  const { addEntity } = useLimitedAction()
  const createEntitiesForOtherFiles = otherFiles.map((file) => () => {
    return addEntity({
      projectId: field.projectId,
      workspaceId: field.workspaceId,
      fields: { [field.propertyId]: { file_name: file.filename, file_url: file.fileUrl } },
    })
  })

  return promiseQueue<APIResult<unknown>>([uploadFirstFile, ...createEntitiesForOtherFiles], {
    maxConcurrent,
  })
}

function uploadFromUrl({ field, url }: { field: Field; url: string }) {
  // eslint-disable-next-line internal/no-call-composable-outside-setup
  const setFieldValue = useSetFieldValue()
  return setFieldValue({
    [field.propertyId]: { field, newValue: { file_name: null, file_url: url } },
  })
}
